<script setup lang="ts" generic="T = string">
import { api } from "@/api";
import { PV } from "@/widgets";
import { useDebounceFn, whenever } from "@vueuse/core";
import { cloneDeep, isFunction, isNotNil, isPlainObject, isString } from "es-toolkit";
import type { SelectFilterEvent } from "primevue";
import { computed, onMounted, reactive, ref, toValue, type UnwrapRef, watch, watchEffect } from "vue";
import type { SelectParam } from "../../common/types.common";
import { defineActions, defineState } from "../common";
import { Buttons } from "../icons";
import { FormExt, FormGuide, FormHead, FormMessage } from "../internal";
import { type BaseProps, defaultProps } from "../props";

interface OptionType {
    label: string;
    value: T;
}

interface OptionTypeUnwrap {
    label: string;
    value: UnwrapRef<T>;
}

type OptionFn = (param?: SelectParam) => Promise<OptionType[]>;

type ModelType = NonNullable<T>;
interface PropsType extends BaseProps<ModelType> {
    options: OptionFn | OptionType[] | readonly OptionType[] | string;
    formId?: string;
    searchable?: boolean;
    placeholder?: string;
    extraChoice?: OptionType;
    stringValue?: (data: T | undefined) => string | undefined;
    externalUrl?: string;
}

const props = withDefaults(defineProps<PropsType>(), {
    ...defaultProps<ModelType>(),
    value: undefined,
    searchable: false,
    stringValue: (data: T | undefined) => {
        if (data === undefined) return undefined;
        if (isPlainObject(data)) {
            const dataWithId = data as { id?: string; [x: string]: unknown };
            if (dataWithId.id !== undefined) {
                return dataWithId.id;
            }
        }

        return data?.toString();
    },
});

const model = defineModel<ModelType | undefined>({
    default: undefined,
});

const state = defineState({
    model: model,
    props: props,
    hasValue: () => model.value !== undefined,
});

const actions = defineActions(model, props, state, {
    onCancel() {
        state.hasError = false;
        state.editMode = false;
        model.value = cloneDeep(toValue(state.original)) as NonNullable<T>;
        filterValue.value = undefined;
    },
    onScramble() {
        if (state.editMode && state.fn.scramble !== undefined) {
            const choices = [...choiceList.value.values()];
            model.value = state.fn.scramble(choices.map((c) => c.value));
        }
    },
});

const choiceList = ref<OptionType[]>([]);
const choiceMap: Map<string | undefined, string> = reactive(new Map());
const currentVal = computed(() =>
    model.value !== undefined ? choiceMap.get(props.stringValue(model.value)) : undefined,
);

const virtualScrollerOptions = computed(() => (choiceList.value.length > 20 ? { itemSize: 42 } : undefined));
const withExtraChoice = ref(false);

const showLoading = ref(false);
const isFiltering = ref(false);
const filterValue = ref<string>();

const emptyMessage = computed(() => {
    return showLoading.value === false ? "Tidak ada pilihan." : "Sedang memuat...";
});
const emptyFilterMessage = computed(() => {
    return isFiltering.value === false ? "Tidak ada pilihan." : "Sedang mencari...";
});

async function populateOptions() {
    let data: OptionType[];

    if (isFunction(props.options)) {
        showLoading.value = true;
        const fn = props.options as OptionFn;
        data = await fn({
            search: filterValue.value,
            current: props.stringValue(model.value),
        });
        showLoading.value = false;
    } else if (isString(props.options)) {
        const req = api.raw.get<OptionType[]>(props.options);
        data = (await req.execute()) ?? [];
    } else {
        data = props.options as OptionType[];
    }

    choiceMap.clear();
    choiceList.value = data;
    data.forEach((e) => choiceMap.set(props.stringValue(e.value), e.label));
    if (props.extraChoice !== undefined && withExtraChoice.value) {
        choiceList.value.unshift(props.extraChoice as OptionTypeUnwrap);
        choiceMap.set(props.stringValue(props.extraChoice.value), props.extraChoice.label);
    }
    isFiltering.value = false;
}

function addExtraChoice() {
    withExtraChoice.value = true;
    model.value = props.extraChoice?.value as NonNullable<T>;
    if (props.extraChoice !== undefined) {
        choiceList.value.unshift(props.extraChoice as OptionTypeUnwrap);
        choiceMap.set(props.stringValue(props.extraChoice.value), props.extraChoice.label);
    }
}

async function onFilter(event: SelectFilterEvent) {
    const filter = event.value;
    if (isNotNil(filter) && filter.trim().length > 0) {
        filterValue.value = filter.trim();
    } else {
        filterValue.value = undefined;
    }

    isFiltering.value = true;
}

async function refreshOptions() {
    await populateOptions();
    if (model.value !== undefined) {
        // Clear model value when current value does not exist in new options.
        if (choiceMap.get(props.stringValue(model.value)) === undefined) {
            model.value = undefined;
        }
    }
}

const refreshFn = useDebounceFn(refreshOptions, 500, {
    maxWait: 2000,
    rejectOnCancel: false,
});

watch(filterValue, refreshFn);
watch(() => props.options, refreshOptions, { immediate: false });

if (!state.editMode) {
    whenever(() => model.value !== undefined, refreshOptions, { once: true, immediate: true });
} else {
    onMounted(populateOptions);
}

if (props.extraChoice !== undefined) {
    watch(
        () => props.stringValue(model.value),
        (v) => {
            const extraValue = props.stringValue(props.extraChoice!.value);
            if (v !== extraValue && choiceList.value[0].value === extraValue) {
                choiceList.value.shift();
                choiceMap.delete(extraValue);
                withExtraChoice.value = false;
            }
        },
    );
}

watchEffect(() => {
    if (model.value === "" || model.value === null) {
        model.value = undefined;
    }
});
</script>

<template>
    <div class="flex flex-col gap-1">
        <FormHead :actions="actions" :externalUrl="props.externalUrl" :param="props" :state="state" />

        <template v-if="!state.editMode">
            <PV.IconField>
                <div
                    v-if="showLoading && model !== undefined"
                    class="rns-g-static-input px-1 py-3"
                    :class="{ 'pr-10': state.updatable }"
                >
                    <PV.Skeleton />
                </div>
                <PV.InputText v-else class="rns-input-static" disabled fluid :value="currentVal" />
                <FormExt :actions="actions" :state="state" />
            </PV.IconField>
        </template>
        <template v-else>
            <PV.Select
                v-model="model"
                :disabled="props.disabled"
                :emptyFilterMessage="emptyFilterMessage"
                :emptyMessage="emptyMessage"
                :filter="props.searchable"
                fluid
                :form="props.formId"
                :inputId="state.id"
                :invalid="state.hasError"
                optionLabel="label"
                :options="choiceList"
                optionValue="value"
                :placeholder="props.placeholder ?? `Pilih ${props.label}`"
                :required="props.required"
                :showClear="state.clearable"
                :virtualScrollerOptions="virtualScrollerOptions"
                @beforeShow="refreshOptions"
                @filter="onFilter"
            >
                <template #clearicon>
                    <Buttons.BtnReset @click="actions.onReset" />
                </template>
                <template v-if="showLoading" #dropdownicon>
                    <i class="pi pi-spin pi-spinner"></i>
                </template>
                <template v-if="props.extraChoice !== undefined && model !== props.extraChoice.value" #header>
                    <PV.Button
                        fluid
                        icon="pi pi-plus"
                        :label="props.extraChoice.label"
                        severity="info"
                        size="small"
                        text
                        @click="addExtraChoice"
                    />
                </template>
            </PV.Select>
        </template>

        <FormGuide :state="state" :text="props.guideText" />
        <FormMessage :state="state" />
    </div>
</template>

<style scoped>
@reference "@/styles/main.css";

.rns-input-static {
    @apply text-gray-700! bg-transparent! shadow-none! px-1! pt-2.25!;
    @apply rns-g-static-input;
}
</style>
